import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { DollarSign, Image, Headphones, BarChart3, UserPlus, Users, Coins } from 'lucide-react';
import { siteConfig } from '@/config/site';

const benefits = [
    {
        icon: DollarSign,
        title: 'Revenue Sharing',
        description: 'Earn generous commissions on every trader you refer. Our competitive rates ensure your efforts are well rewarded.',
    },
    {
        icon: Image,
        title: 'Marketing Materials',
        description: 'Access professionally designed banners, landing pages, and promotional content to maximize your conversions.',
    },
    {
        icon: Headphones,
        title: 'Dedicated Manager',
        description: 'Get a personal partner manager who provides strategic guidance, optimization tips, and priority support.',
    },
    {
        icon: BarChart3,
        title: 'Real-Time Tracking',
        description: 'Monitor your referrals, conversions, and earnings in real-time through your comprehensive partner dashboard.',
    },
];

const steps = [
    {
        icon: UserPlus,
        step: '01',
        title: 'Sign Up',
        description: 'Register for our partner program in minutes. No fees, no complicated requirements — just fill out the application form.',
    },
    {
        icon: Users,
        step: '02',
        title: 'Refer Traders',
        description: 'Share your unique referral link with your audience. Use our marketing materials to drive conversions across any channel.',
    },
    {
        icon: Coins,
        step: '03',
        title: 'Earn Commissions',
        description: 'Earn commissions on every successful referral. Track your earnings in real-time and withdraw whenever you want.',
    },
];

export default function Partners() {
    return (
        <AppLayout title={`Partner Program - ${siteConfig.brand.name}`} description={`Join the ${siteConfig.brand.name} partner program and earn commissions by referring traders.`}>
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Partners</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                            Partner <span className="text-gradient">Program</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
                            Join our affiliate network and earn competitive commissions by referring traders to {siteConfig.brand.name}.
                            Build a sustainable income stream with one of the industry's most rewarding partner programs.
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <Button size="lg">Become a Partner</Button>
                    </FadeIn>
                </div>
            </Section>

            {/* Benefits */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Partner"
                        highlight="Benefits"
                        description={`Everything you need to succeed as a ${siteConfig.brand.name} partner.`}
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {benefits.map((benefit) => (
                            <motion.div key={benefit.title} variants={staggerItem}>
                                <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-0 h-full text-center">
                                    <CardContent className="p-6 pt-6">
                                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-primary/10 border border-primary/10 text-primary mb-5">
                                            <benefit.icon className="h-7 w-7" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-foreground mb-2">{benefit.title}</h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </StaggerChildren>
            </Section>

            {/* How It Works */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="How It"
                        highlight="Works"
                        description="Start earning in three simple steps."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
                        {steps.map((step, index) => (
                            <motion.div key={step.title} variants={staggerItem} className="relative text-center">
                                {index < steps.length - 1 && (
                                    <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-px bg-border" />
                                )}
                                <div className="inline-flex items-center justify-center w-20 h-20 rounded-lg bg-primary/10 border border-primary/10 text-primary mb-6 relative">
                                    <step.icon className="h-9 w-9" />
                                    <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">
                                        {step.step}
                                    </span>
                                </div>
                                <h3 className="text-xl font-semibold text-foreground mb-3">{step.title}</h3>
                                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 tracking-tight">
                            Ready to <span className="text-gradient">Earn?</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed">
                            Apply to our partner program today and start earning commissions with every referral.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Button size="lg">Register Now</Button>
                            <Button size="lg" variant="outline">
                                Contact Us
                            </Button>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
